
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Play, ArrowRight, Users, TrendingUp, Heart, Award, Target, CheckCircle, MessageSquare, FileText, BarChart3, Clock } from "lucide-react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import AwardsSection from "@/components/trust/AwardsSection";
import jessicaPhoto from "@/assets/case-studies/jessica-redmond.jpeg";

const EmpowerEveryManager = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <ProductPageLayout
      title="Empower Every Manager - Manager Tools & Development Platform"
      description="Give managers the tools they need to succeed with Assembly's comprehensive manager platform. From 1:1s to team insights, empower great leadership at every level."
      keywords="manager tools, 1:1 meetings, manager development, team leadership, manager dashboard"
      ctaTitle="Ready to Empower Your Managers?"
      ctaDescription="See how Assembly helps companies develop confident, effective managers who drive team success."
      ctaButtonText="Start Free Trial"
      ctaVariant="blue"
      showCTA={false}
    >
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <Badge className="mb-6 bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200 text-sm font-medium px-4 py-2">
                MANAGER DEVELOPMENT
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Empower Every
                <br />
                <span className="text-blue-600">Manager</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-lg leading-relaxed">
                Transform managers from task-focused to people-focused leaders with tools, 
                insights, and support that drive team engagement and performance.
              </p>
              <div className="flex justify-start">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-medium text-lg"
                  onClick={() => setIsDemoModalOpen(true)}
                >
                  Book Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            {/* Manager Dashboard Mockup */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 relative max-w-md ml-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Manager Dashboard</h3>
                    <p className="text-gray-500 text-sm">Team Performance Overview</p>
                  </div>
                </div>
                
                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">94%</div>
                    <div className="text-sm text-green-700">Team Engagement</div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">8</div>
                    <div className="text-sm text-blue-700">1:1s This Week</div>
                  </div>
                </div>
                
                {/* Upcoming 1:1s */}
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-3">Upcoming 1:1s</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                      <img src={jessicaPhoto} alt="Sarah" className="w-8 h-8 rounded-full" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">Sarah Johnson</div>
                        <div className="text-xs text-gray-500">Today 2:00 PM</div>
                      </div>
                      <Clock className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                      <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face" alt="Mike" className="w-8 h-8 rounded-full" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">Mike Chen</div>
                        <div className="text-xs text-gray-500">Tomorrow 10:00 AM</div>
                      </div>
                      <Clock className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-blue-100 text-blue-700 hover:bg-blue-200">
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Give Feedback
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1">
                    <BarChart3 className="h-4 w-4 mr-1" />
                    View Insights
                  </Button>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                  <Target className="h-5 w-5 text-white" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Logos Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12 sm:mb-16">
            <motion.h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6" {...fadeInUp}>
              You'll be in good company.
            </motion.h2>
            <motion.p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto" {...fadeInUp}>Many top organizations leverage Assembly as a Digital HQ to support their transition to hybrid work and nurture a positive workplace culture.</motion.p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center opacity-60">
            {[{
            name: "StubHub",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393801e8e212d1d42ff_stubhub-gray.svg"
          }, {
            name: "1-800-Flowers",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/6386539332627ece447341d4_1800flowers-gray.svg"
          }, {
            name: "Coca-Cola",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/638653928ded58181120ad86_Coca-Cola-gray.svg"
          }, {
            name: "Amazon",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393cd30f0e52523af12_Amazon-gray.svg"
          }, {
            name: "PODS",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393ed49964f0f60c3c7_PODS-gray.svg"
          }, {
            name: "Allbirds",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393708a2292ab2159f4_Allbirds-gray.svg"
          }].map((logo, index) => <motion.div key={index} className="flex items-center justify-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }}>
                <img src={logo.src} alt={`${logo.name} logo`} className="h-8 w-auto object-contain" />
              </motion.div>)}
          </div>
          
          <div className="mt-12 sm:mt-16">
            <AwardsSection />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <motion.div className="space-y-2" {...fadeInUp}>
              <div className="text-4xl font-bold text-blue-600">89%</div>
              <div className="text-gray-600">Manager confidence</div>
            </motion.div>
            <motion.div className="space-y-2" {...fadeInUp}>
              <div className="text-4xl font-bold text-green-600">76%</div>
              <div className="text-gray-600">Team performance</div>
            </motion.div>
            <motion.div className="space-y-2" {...fadeInUp}>
              <div className="text-4xl font-bold text-purple-600">82%</div>
              <div className="text-gray-600">Employee satisfaction</div>
            </motion.div>
            <motion.div className="space-y-2" {...fadeInUp}>
              <div className="text-4xl font-bold text-orange-600">4x</div>
              <div className="text-gray-600">Better 1:1 quality</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem vs Solution */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Why Good Managers Struggle
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Most managers are promoted for technical skills but lack the tools and training to lead people effectively.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <motion.div 
              className="bg-red-50 border-2 border-red-200 rounded-2xl p-8"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">😰</span>
                </div>
                <h3 className="text-2xl font-bold text-red-800">Struggling Managers</h3>
              </div>
              <ul className="space-y-4 text-red-700">
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">No clear management framework</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">Inconsistent 1:1 meetings</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">Limited team visibility</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">Reactive instead of proactive</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 mt-1 text-lg">✗</span>
                  <span className="text-lg">Lack of development support</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-green-50 border-2 border-green-200 rounded-2xl p-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💪</span>
                </div>
                <h3 className="text-2xl font-bold text-green-800">Empowered Leaders</h3>
              </div>
              <ul className="space-y-4 text-green-700">
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Structured management tools</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Guided 1:1 frameworks</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Real-time team insights</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Proactive coaching support</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 mt-1 text-lg">✓</span>
                  <span className="text-lg">Continuous skill development</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Tools That Make Great Managers
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Give every manager the confidence and capability to lead their team to success.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Structured 1:1s</h3>
              <p className="text-gray-600 leading-relaxed">
                Never have an awkward 1:1 again. Guided templates and conversation starters help build stronger relationships.
              </p>
            </motion.div>

            <motion.div 
              className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Team Insights</h3>
              <p className="text-gray-600 leading-relaxed">
                Get real-time visibility into team engagement, performance trends, and potential issues before they escalate.
              </p>
            </motion.div>

            <motion.div 
              className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Goal Tracking</h3>
              <p className="text-gray-600 leading-relaxed">
                Set clear expectations and track progress with transparent goal management that keeps everyone aligned.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              From Manager to Leader
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Simple steps to transform your management approach and unlock your team's potential.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Set Up Your Dashboard</h3>
              <p className="text-gray-600 leading-relaxed">
                Get instant visibility into your team's engagement, performance, and development needs with personalized insights.
              </p>
            </motion.div>

            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Lead Better 1:1s</h3>
              <p className="text-gray-600 leading-relaxed">
                Use guided templates and conversation frameworks to have more meaningful, productive one-on-one meetings.
              </p>
            </motion.div>

            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Drive Team Success</h3>
              <p className="text-gray-600 leading-relaxed">
                Watch engagement and performance improve as your team feels more supported, valued, and aligned with goals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Photos Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Managers Making a Difference
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              See how empowered managers are transforming their teams and driving better results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="relative rounded-2xl overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=300&fit=crop" 
                alt="Manager coaching session" 
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="font-bold text-lg">Coaching Excellence</h3>
                  <p className="text-sm opacity-90">Developing future leaders</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="relative rounded-2xl overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=300&fit=crop" 
                alt="Team collaboration" 
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="font-bold text-lg">Team Building</h3>
                  <p className="text-sm opacity-90">Stronger connections</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="relative rounded-2xl overflow-hidden group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=300&fit=crop" 
                alt="Performance review" 
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                <div className="text-white">
                  <h3 className="font-bold text-lg">Performance Growth</h3>
                  <p className="text-sm opacity-90">Data-driven development</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Lead with Confidence?
            </h2>
            <p className="text-blue-100 text-xl mb-8 max-w-3xl mx-auto">
              Join thousands of managers using Assembly to build stronger teams, drive better performance, and become the leader their people deserve.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-gray-100 px-10 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              onClick={() => setIsDemoModalOpen(true)}
            >
              Book Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>
      
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </ProductPageLayout>
  );
};

export default EmpowerEveryManager;
